import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import Section from '../components/Section';
import Card from '../components/Card';
import { Mail, Phone, MapPin, Send } from 'lucide-react';
import { fetchContactInfo, fetchContactPage, fetchSiteSettings } from '../lib/sanity';
import { defaultContactInfo, defaultContactPage } from '../lib/defaultContent';
import GrainBackground from '../components/GrainBackground';
import { useGrainAnimation } from '../hooks/useGrainAnimation';
import { submitToSanity } from '../services/api';

const Contact: React.FC = () => {
  const { grainAnimationEnabled } = useGrainAnimation();
  const [searchParams] = useSearchParams();

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    subject: searchParams.get('subject') || '',
    message: '',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      await submitToSanity('contactSubmission', {
        name: formData.name,
        email: formData.email,
        company: formData.company,
        phone: formData.phone,
        subject: formData.subject,
        message: formData.message,
      });

      setSubmitStatus('success');
      setFormData({ name: '', email: '', company: '', phone: '', subject: '', message: '' });

      setTimeout(() => {
        setSubmitStatus('idle');
      }, 5000);
    } catch (error) {
      console.error('Submission error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const [contactInfo, setContactInfo] = useState(defaultContactInfo);
  const [contactPage, setContactPage] = useState(defaultContactPage);
  const [whatsappNumber, setWhatsappNumber] = useState<string>('+256759177625');

  useEffect(() => {
    // Prioritize fetched data, fallback to defaults only if fetch fails
    fetchContactInfo().then((data) => {
      setContactInfo(data || defaultContactInfo);
    });
    fetchContactPage().then((data) => {
      setContactPage(data || defaultContactPage);
    });
    fetchSiteSettings().then((data) => {
      if (data?.whatsappNumber) setWhatsappNumber(data.whatsappNumber);
    });
  }, []);

  const contactCards = [
    {
      icon: Mail,
      title: 'Email',
      content: contactInfo.email,
      link: `mailto:${contactInfo.email}`,
      isWhatsApp: false,
    },
    {
      icon: null,
      title: 'WhatsApp',
      content: whatsappNumber,
      link: `https://wa.me/${whatsappNumber.replace(/[^0-9]/g, '')}?text=${encodeURIComponent('Hi! I would like to inquire about your products and services.')}`,
      isWhatsApp: true,
    },
    {
      icon: Phone,
      title: 'Phone',
      content: contactInfo.phone,
      link: `tel:${contactInfo.phone.replace(/\s+/g, '')}`,
      isWhatsApp: false,
    },
    {
      icon: MapPin,
      title: 'Head Office',
      content: contactInfo.address,
      link: null,
      isWhatsApp: false,
    },
  ];

  return (
    <main className="relative pt-20">
      {grainAnimationEnabled && <GrainBackground />}
      <Section padding="default">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="max-w-4xl mx-auto text-center mb-16"
        >
          <h1 className="text-5xl md:text-6xl font-playfair font-bold text-primary-black mb-6">
            {contactPage.headerTitle} <span className="text-primary-red">{contactPage.headerHighlight}</span>
          </h1>
          <p className="text-xl md:text-2xl text-gray-700 leading-relaxed">
            {contactPage.headerDescription}
          </p>
        </motion.div>

        {/* Media strip — image + video + image */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.2 }}
          className="flex gap-4 mb-16"
          style={{ height: '280px' }}
        >
          {contactPage.mediaImages.length >= 1 && (
            <div className="flex-1 rounded-2xl overflow-hidden shadow-md hidden md:block">
              <img
                src={contactPage.mediaImages[0]}
                alt="Contact visual 1"
                className="w-full h-full object-cover"
              />
            </div>
          )}
          {contactPage.mediaImages.length >= 2 && (
            <div className="flex-[2] rounded-2xl overflow-hidden shadow-md">
              <img
                src={contactPage.mediaImages[1]}
                alt="Contact visual 2"
                className="w-full h-full object-cover"
              />
            </div>
          )}
          {contactPage.mediaImages.length >= 3 && (
            <div className="flex-1 rounded-2xl overflow-hidden shadow-md hidden md:block">
              <img
                src={contactPage.mediaImages[2]}
                alt="Contact visual 3"
                className="w-full h-full object-cover"
              />
            </div>
          )}
        </motion.div>

        {/* Form + Why Choose Revian */}
        <div className="grid lg:grid-cols-2 gap-12 items-start mb-16">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl md:text-4xl font-playfair font-bold text-primary-black mb-6">
              {contactPage.formTitle}
            </h2>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label
                  htmlFor="subject"
                  className="block text-sm font-inter font-medium text-gray-700 mb-2"
                >
                  Subject
                </label>
                <input
                  type="text"
                  id="subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-red focus:border-transparent transition-all"
                  placeholder="e.g. Bulk Quote – Rice"
                />
              </div>

              <div>
                <label
                  htmlFor="name"
                  className="block text-sm font-inter font-medium text-gray-700 mb-2"
                >
                  Full Name
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-red focus:border-transparent transition-all"
                  placeholder="John Doe"
                />
              </div>

              <div>
                <label
                  htmlFor="email"
                  className="block text-sm font-inter font-medium text-gray-700 mb-2"
                >
                  Email Address
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-red focus:border-transparent transition-all"
                  placeholder="john@company.com"
                />
              </div>

              <div>
                <label
                  htmlFor="company"
                  className="block text-sm font-inter font-medium text-gray-700 mb-2"
                >
                  Company Name
                </label>
                <input
                  type="text"
                  id="company"
                  name="company"
                  value={formData.company}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-red focus:border-transparent transition-all"
                  placeholder="Your Company Ltd"
                />
              </div>

              <div>
                <label
                  htmlFor="phone"
                  className="block text-sm font-inter font-medium text-gray-700 mb-2"
                >
                  Phone Number
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-red focus:border-transparent transition-all"
                  placeholder="+256 700 000 000"
                />
              </div>

              <div>
                <label
                  htmlFor="message"
                  className="block text-sm font-inter font-medium text-gray-700 mb-2"
                >
                  Message
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={5}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-red focus:border-transparent transition-all resize-none"
                  placeholder="Tell us about your requirements..."
                />
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full px-8 py-4 bg-primary-red hover:bg-red-700 text-white font-inter font-semibold rounded-lg shadow-md hover:shadow-lg transition-all duration-200 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? (
                  'Sending...'
                ) : (
                  <>
                    {contactPage.submitButtonText}
                    <Send className="w-5 h-5" />
                  </>
                )}
              </button>

              {submitStatus === 'success' && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="p-4 bg-secondary-sage/20 border border-secondary-sage rounded-lg text-center"
                >
                  <p className="text-gray-700 font-medium">
                    {contactPage.successMessage}
                  </p>
                </motion.div>
              )}

              {submitStatus === 'error' && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="p-4 bg-red-50 border border-red-300 rounded-lg text-center"
                >
                  <p className="text-red-700 font-medium">
                    Failed to send message. Please try again or contact us directly.
                  </p>
                </motion.div>
              )}
            </form>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="lg:sticky lg:top-24"
          >
            <Card>
              <h3 className="text-2xl font-playfair font-bold text-primary-black mb-4">
                {contactPage.whyChooseTitle}
              </h3>
              <ul className="space-y-4">
                {contactPage.whyChoosePoints.map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <span className="w-2 h-2 rounded-full bg-primary-red flex-shrink-0 mt-2" />
                    <span className="text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
            </Card>
          </motion.div>
        </div>

        {/* Contact info cards */}
        <div className="grid lg:grid-cols-4 gap-8">
          {contactCards.map((info, index) => {
            const IconComponent = info.icon;
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
              >
                <Card>
                  <div className="flex flex-col items-center text-center">
                    <div className={`w-16 h-16 rounded-full ${info.isWhatsApp ? 'bg-[#25D366]/10' : 'bg-primary-red/10'} flex items-center justify-center mb-4`}>
                      {info.isWhatsApp ? (
                        <img src="/icons/whatsapp.svg" alt="" className="w-8 h-8" />
                      ) : (
                        IconComponent ? <IconComponent className="w-8 h-8 text-primary-red" /> : null
                      )}
                    </div>
                    <h3 className="text-lg font-playfair font-bold text-primary-black mb-2">
                      {info.title}
                    </h3>
                    {info.link ? (
                      <a
                        href={info.link}
                        target={info.isWhatsApp ? "_blank" : undefined}
                        rel={info.isWhatsApp ? "noopener noreferrer" : undefined}
                        className="text-gray-700 hover:text-primary-red transition-colors"
                      >
                        {info.content}
                      </a>
                    ) : (
                      <p className="text-gray-700">{info.content}</p>
                    )}
                  </div>
                </Card>
              </motion.div>
            );
          })}
        </div>
      </Section>
    </main>
  );
};

export default Contact;
